/**
 * useDraggablePanel — make a floating panel draggable by a grip handle, within
 * its offsetParent (or the viewport for a position:fixed panel), honoring the
 * off-screen limits below. Pointer-based (mouse + touch). Shared by the Add
 * palette and the canvas tools panel.
 */
import { useRef, useState } from "react";

// How far the panel may slide off: 80% of its width off the left/right edges
// (20% stays visible), the top edge pinned, 90% of its height off the bottom
// (10% stays reachable).
const OFF_X = 0.8;
const OFF_BOTTOM = 0.9;

/**
 * Optional drag lifecycle hooks — used by the dockable panels (Add palette +
 * format toolbar) to hit-test the pointer against the OTHER panel while
 * dragging (`onMove`) and to commit/cancel a dock on release (`onDrop`). Both
 * receive/observe viewport-client coordinates so they work across the two
 * panels' different coordinate systems (canvas-absolute vs viewport-fixed).
 */
interface DragHooks {
  onMove?: (pointer: { x: number; y: number }) => void;
  onDrop?: (pointer: { x: number; y: number }) => void;
}

export function useDraggablePanel(
  initial: { x: number; y: number },
  hooks?: DragHooks,
) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState(initial);
  // Keep the latest hooks reachable from the long-lived pointer listeners
  // without re-subscribing them (onHandleDown closes over this ref, not the
  // hooks object, so a re-render mid-drag can't drop a stale callback).
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;

  const onHandleDown = (e: React.PointerEvent) => {
    const el = ref.current;
    if (!el) return;
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      /* best-effort */
    }
    const startPointer = { x: e.clientX, y: e.clientY };
    const startPos = { x: pos.x, y: pos.y };

    const onMove = (ev: PointerEvent) => {
      // A position:fixed panel has no offsetParent → clamp against the viewport.
      const parent = el.offsetParent as HTMLElement | null;
      const W = parent?.clientWidth ?? window.innerWidth;
      const H = parent?.clientHeight ?? window.innerHeight;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      let x = startPos.x + (ev.clientX - startPointer.x);
      let y = startPos.y + (ev.clientY - startPointer.y);
      x = Math.max(-OFF_X * w, Math.min(W - (1 - OFF_X) * w, x));
      y = Math.max(0, Math.min(H - (1 - OFF_BOTTOM) * h, y));
      setPos({ x, y });
      hooksRef.current?.onMove?.({ x: ev.clientX, y: ev.clientY });
    };
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      hooksRef.current?.onDrop?.({ x: ev.clientX, y: ev.clientY });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  return { ref, pos, setPos, onHandleDown };
}
